import type { Building } from "building-types/building/buildingTypes";
import type { BuildingAnalyticEntity, BuildingAnalyticEntityStyle } from "building-types/buildingAnalyticEntityTypes";
import type { BuildingGeomParent } from "building-types/buildingGeomTag/buildingGeomTagTypes";
import {
  CLIP_EXTERIOR_FINISH,
  DOOR,
  DOOR_TRIM,
  EXTERIOR,
  INTERIOR,
  OPEN,
  TAG_POSITION_BACK,
  TAG_POSITION_FRONT,
  trimTypeTag,
} from "building-types/buildingGeomTag/buildingGeomTagTypes";
import type { BuildingGeomTag } from "building-types/buildingGeomTag/buildingGeomTagTypes";
import type { OpeningTrim } from "building-types/casing/openingTrimTypes";
import { doorOperationTypeIsOpen } from "building-types/doors/doorOperationTypes";
import type { DoorUnit, GeneratedDoorPlacement } from "building-types/doors/doorTypes";
import type { BuildingEntityHandle } from "building-types/entities/buildingEntityHandle";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { WallOpening } from "building-types/openings/wallOpeningTypes";
import type { RWTable, Table } from "common/data/table";
import { percentNew, worldInchesNew } from "common/utils/nominalNumbers";
import { isInteriorRoomEnclosure } from "doc/rooms/roomAttributesTools";
import { faceNew } from "geom/brep";
import { polyLine2OnPlane } from "geom/plane";
import { doorTrimClipsExteriorFinish } from "../elements/doors/doorQuery";
import { isGarageDoorOperationType } from "../elements/doors/doorUtils";
import { addAnalyticFace, addPolyLine3Edges } from "../utils/generateUtil";
import makeBuildingSelector from "../utils/makeBuildingSelector";
import memo from "../utils/memo";

const openingSurfaceStyle: BuildingAnalyticEntityStyle = {
  color: 0x00ffff,
  opacity: percentNew(0.1),
  lineWidth3d: worldInchesNew(0.1),
};

const trimLineStyle: BuildingAnalyticEntityStyle = {
  color: 0xddaa22,
  opacity: percentNew(0.5),
  lineWidth3d: worldInchesNew(4),
};

const trimSurfaceStyle: BuildingAnalyticEntityStyle = {
  color: 0xddaa22,
  edgeColor: 0xddaa22,
  opacity: percentNew(0.25),
};

function addExteriorTrimAnalyticEntities(
  entities: RWTable<BuildingAnalyticEntity>,
  door: DoorUnit,
  parents: DocEntityHandle[],
  opening: WallOpening,
  trim: OpeningTrim,
  sideTag: BuildingGeomTag,
) {
  const doorTag: BuildingGeomParent = {
    type: "PARENT",
    parentType: "DOOR",
    parentId: door.doorPlacement.id,
  };
  const trimTag: BuildingGeomParent = {
    type: "PARENT",
    parentType: "EXTERIOR_OPENING_TRIM",
    parentId: trim.id,
  };
  const trimTags: BuildingGeomTag[] = [DOOR_TRIM, doorTag, sideTag, trimTag, trimTypeTag(trim.type)];
  if (doorTrimClipsExteriorFinish(door, trim.type)) {
    trimTags.push(CLIP_EXTERIOR_FINISH);
  }

  if (trim.rail) {
    const rail3 = polyLine2OnPlane(opening.backPlane, trim.rail);
    addPolyLine3Edges(
      "ANALYTIC_DOOR_TRIM",
      entities,
      "Door " + door.doorPlacement.descriptor.reportedName + " " + trim.type,
      parents,
      rail3,
      trimTags,
      trimLineStyle,
    );
  }

  if (trim.elevationProfile && trim.elevationProfile.length > 0) {
    const face = faceNew(trim.elevationProfile, opening.backPlane);
    addAnalyticFace(
      "ANALYTIC_DOOR_TRIM",
      entities,
      "Door " + door.doorPlacement.descriptor.reportedName + " " + trim.type + " surface",
      parents,
      face,
      undefined,
      trimTags,
      trimSurfaceStyle,
      trim.elevationProfileEdgeTypes,
    );
  }
}

type Access = "interior" | "exterior" | "garage";

function doorAccess(door: GeneratedDoorPlacement): Access {
  const { frontRoom, backRoom } = door;
  if (isGarageDoorOperationType(door)) {
    return "garage";
  }
  if (!frontRoom || !isInteriorRoomEnclosure[frontRoom.attributes.roomEnclosure]) {
    return "exterior";
  }
  if (!backRoom || !isInteriorRoomEnclosure[backRoom.attributes.roomEnclosure]) {
    return "exterior";
  }
  return "interior";
}

function doorOpeningSurfaceTags(door: GeneratedDoorPlacement, access: Access): BuildingGeomTag[] {
  const tags = access === "interior" ? [DOOR, INTERIOR] : [DOOR, EXTERIOR];
  if (doorOperationTypeIsOpen[door.descriptor.operationType] === true) {
    tags.push(OPEN);
  }

  return tags;
}

export function addDoorAnalyticEntities(
  building: ReturnType<typeof select>,
  buildingEntityHandle: BuildingEntityHandle,
): Table<BuildingAnalyticEntity> {
  const entities: RWTable<BuildingAnalyticEntity> = {};

  const door = building.doorUnits[buildingEntityHandle.id];
  if (door?.entityType !== "DOOR" || !door?.doorPlacement?.opening) {
    return entities;
  }

  const handle: DocEntityHandle = {
    type: "GENERATED_ENTITY",
    entity: buildingEntityHandle,
  };

  const parents: DocEntityHandle[] = [handle];

  if (door.docEntityHandle) {
    parents.push(door.docEntityHandle);
  }

  if (door.doorPlacement.handle) {
    parents.push(door.doorPlacement.handle);
  }

  const opening = door.doorPlacement.opening;
  const access = doorAccess(door.doorPlacement);
  const openingSurfaceTags = doorOpeningSurfaceTags(door.doorPlacement, access);

  if (access === "exterior" || access === "garage") {
    for (const trim of opening?.back?.trim) {
      addExteriorTrimAnalyticEntities(entities, door, parents, opening, trim, TAG_POSITION_BACK);
    }
  }

  const frontOpening = opening.front;
  if (frontOpening) {
    const face = faceNew([frontOpening.opening2], opening.frontPlane);
    addAnalyticFace(
      "ANALYTIC_DOOR_TRIM",
      entities,
      "Door front opening",
      parents,
      face,
      undefined,
      [TAG_POSITION_FRONT, ...openingSurfaceTags],
      {
        ...openingSurfaceStyle,
      },
      frontOpening.opening2EdgeTypes ? [frontOpening.opening2EdgeTypes] : undefined,
    );
  }
  const backOpening = opening.back;
  if (backOpening) {
    const face = faceNew([backOpening.opening2], opening.backPlane);
    addAnalyticFace(
      "ANALYTIC_DOOR_TRIM",
      entities,
      "Door back opening",
      parents,
      face,
      undefined,
      [TAG_POSITION_BACK, ...openingSurfaceTags],
      {
        ...openingSurfaceStyle,
      },
      backOpening.opening2EdgeTypes ? [backOpening.opening2EdgeTypes] : undefined,
    );
  }

  return entities;
}

const select = makeBuildingSelector(["doorUnits"]);

export function generate(building: ReturnType<typeof select>): Pick<Building, "analyticDoorTrimEntities"> {
  const analyticDoorTrimEntities: RWTable<BuildingAnalyticEntity> = {};
  for (const id of Object.keys(building.doorUnits)) {
    Object.assign(analyticDoorTrimEntities, addDoorAnalyticEntities(building, { entityType: "DOOR", id }));
  }
  return { analyticDoorTrimEntities };
}

export default memo(select, generate);
